import React, { useEffect, useState } from 'react';
import { api } from '../api';
import SheepModal from '../components/SheepModal';
import HealthTab from '../components/HealthTab';
import WeightTab from '../components/WeightTab';
import ReproductionTab from '../components/ReproductionTab';
import { formatMoney } from '../utils/currency';
import { ageFromDob } from '../utils/format';

const TABS = ['Overview', 'Health', 'Weight', 'Reproduction', 'Financials'];

export default function SheepProfile({ sheepId, onNavigate }) {
  const [sheep, setSheep] = useState(null);
  const [lineage, setLineage] = useState([]);
  const [offspring, setOffspring] = useState([]);
  const [tab, setTab] = useState('Overview');
  const [showEdit, setShowEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setError('');
    Promise.all([api.getSheep(sheepId), api.getLineage(sheepId), api.getOffspring(sheepId)])
      .then(([sheepData, lineageData, offspringData]) => {
        setSheep(sheepData);
        setLineage(lineageData);
        setOffspring(offspringData);
      })
      .catch(err => setError(err.message));
  };

  useEffect(() => { load(); setTab('Overview'); }, [sheepId]);

  const handleDelete = async () => {
    try {
      await api.deleteSheep(sheepId);
      onNavigate('flock');
    } catch (err) {
      setError(err.message);
    }
  };

  if (!sheep) return <div className="page">{error ? <div className="alert alert-danger">{error}</div> : <p className="text-muted">Loading…</p>}</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="profile-title-row">
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('flock')}>← Flock</button>
            <h1 className="page-title">{sheep.tag_id}</h1>
            <span className={`badge badge-${sheep.status}`}>{sheep.status}</span>
            <span className={`badge badge-${sheep.sex}`}>{sheep.sex}</span>
          </div>
          <p className="page-subtitle">{sheep.breed || 'Unknown breed'} · Born {sheep.date_of_birth || 'unknown date'}</p>
        </div>
        <div className="profile-actions">
          <button className="btn btn-ghost" onClick={() => window.print()}>Print</button>
          <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>Edit</button>
          <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>Delete</button>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-4">{error}</div>}

      <div className="tabs">
        {TABS.map(t => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Overview' && (
        <div className="form-grid form-grid-2" style={{ gap: 20 }}>
          <div className="card">
            <div className="section-title mb-4">Identity</div>
            <dl style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px 0', fontSize: 14 }}>
              {[
                ['Tag ID', sheep.tag_id],
                ['Breed', sheep.breed || '—'],
                ['Sex', sheep.sex],
                ['Date of Birth', sheep.date_of_birth || '—'],
                ['Age', sheep.date_of_birth ? ageFromDob(sheep.date_of_birth) : '—'],
                ['Status', sheep.status],
                ['Litter Size', sheep.litter_size || '—'],
                ...(sheep.status === 'deceased'
                  ? [['Date of Death', sheep.death_date || '—'], ['Cause', sheep.death_reason || '—']]
                  : []),
              ].map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt style={{ color: 'var(--slate-light)', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</dt>
                  <dd style={{ fontWeight: 500 }}>{v}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>

          <div className="card">
            <div className="section-title mb-4">Maternal Lineage</div>
            {lineage.length <= 1 ? (
              <p className="text-muted">No dam recorded.</p>
            ) : (
              <div className="lineage-chain">
                {lineage.map((node, i) => (
                  <React.Fragment key={node.id}>
                    {i > 0 && <div className="lineage-connector" />}
                    <div
                      className="lineage-node"
                      onClick={() => i > 0 && onNavigate('profile', node.id)}
                      style={i === 0 ? { background: 'var(--parchment)', fontWeight: 700 } : {}}
                    >
                      <span style={{ fontSize: 18 }}>{i === 0 ? '🐑' : '↑'}</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{node.tag_id}</div>
                        <div className="text-muted">{node.breed || 'Unknown breed'} · {i === 0 ? 'This sheep' : `Generation ${i}`}</div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {offspring.length > 0 && (
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="section-title mb-4">Offspring ({offspring.length})</div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Tag ID</th><th>Breed</th><th>Sex</th><th>Date of Birth</th><th>Status</th></tr></thead>
                  <tbody>
                    {offspring.map(o => (
                      <tr key={o.id} onClick={() => onNavigate('profile', o.id)}>
                        <td data-label="Tag ID"><strong>{o.tag_id}</strong></td>
                        <td data-label="Breed">{o.breed || '—'}</td>
                        <td data-label="Sex"><span className={`badge badge-${o.sex}`}>{o.sex}</span></td>
                        <td data-label="Date of Birth">{o.date_of_birth || '—'}</td>
                        <td data-label="Status"><span className={`badge badge-${o.status}`}>{o.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {sheep.notes && (
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="section-title mb-2">Notes</div>
              <p style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{sheep.notes}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'Health' && (
        <div className="card"><HealthTab sheepId={sheepId} /></div>
      )}

      {tab === 'Weight' && (
        <div className="card"><WeightTab sheepId={sheepId} /></div>
      )}

      {tab === 'Reproduction' && (
        <div className="card"><ReproductionTab sheepId={sheepId} sex={sheep.sex} /></div>
      )}

      {tab === 'Financials' && (
        <div className="card">
          <div className="section-title mb-4">💰 Financials</div>
          <div className="form-grid form-grid-2" style={{ gap: 24 }}>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Purchase</p>
              <dl style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px 0', fontSize: 14 }}>
                {[['Date', sheep.purchase_date || '—'], ['Price', sheep.purchase_price != null ? formatMoney(sheep.purchase_price) : '—'], ['Source', sheep.purchase_source || '—']].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt style={{ color: 'var(--slate-light)', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>{k}</dt>
                    <dd>{v}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Sale</p>
              <dl style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px 0', fontSize: 14 }}>
                {[['Date', sheep.sale_date || '—'], ['Price', sheep.sale_price != null ? formatMoney(sheep.sale_price) : '—'], ['Buyer', sheep.sale_buyer || '—']].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt style={{ color: 'var(--slate-light)', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>{k}</dt>
                    <dd>{v}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Projected Value</p>
              <dl style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px 0', fontSize: 14 }}>
                {[['Market Value', sheep.market_value != null ? formatMoney(sheep.market_value) : '—'], ['Used In Estimate', sheep.sale_price != null ? 'Sale price' : sheep.market_value != null ? 'Market value' : '—']].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt style={{ color: 'var(--slate-light)', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>{k}</dt>
                    <dd>{v}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          </div>
          {sheep.purchase_price != null && sheep.sale_price != null && (
            <div className="divider" />
          )}
          {sheep.purchase_price != null && sheep.sale_price != null && (
            <p style={{ fontSize: 15 }}>
              <strong>Profit / Loss: </strong>
              <span style={{ color: sheep.sale_price >= sheep.purchase_price ? 'var(--moss)' : 'var(--rust)', fontWeight: 700 }}>
                {formatMoney(sheep.sale_price - sheep.purchase_price, { showPositiveSign: true })}
              </span>
            </p>
          )}
          <div className="mt-4">
            <button className="btn btn-secondary btn-sm" onClick={() => setShowEdit(true)}>Edit Financial Details</button>
          </div>
        </div>
      )}

      {showEdit && (
        <SheepModal sheep={sheep} onClose={() => setShowEdit(false)} onSaved={(s) => { setSheep(s); setShowEdit(false); load(); }} />
      )}

      {confirmDelete && (
        <div className="modal-backdrop" onClick={() => setConfirmDelete(false)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: 12 }}>Delete {sheep.tag_id}?</h2>
            <p style={{ fontSize: 14, color: 'var(--slate-light)' }}>This will permanently delete the sheep and all associated records. This cannot be undone.</p>
            <div className="flex gap-2 mt-4" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
